import { useCallback, useEffect, useState } from 'react';
import Modal from '@/components/base/Modal';
import { supabase } from '@/lib/supabase';
import { LEVEL_CATEGORIES } from '@/lib/levels';

interface Level {
  id: string;
  name: string;
  category: string | null;
  description: string | null;
  sort_order: number;
  active: boolean;
}

export default function LevelSettings() {
  const [levels, setLevels] = useState<Level[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editLevel, setEditLevel] = useState<Level | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Level | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const fetchLevels = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('zehrahoca_levels')
        .select('*')
        .order('sort_order', { ascending: true });
      if (fetchError) throw fetchError;
      setLevels((data ?? []) as Level[]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLevels();
  }, [fetchLevels]);

  function handleNew() {
    setEditLevel(null);
    setFormError(null);
    setModalOpen(true);
  }

  function handleEdit(l: Level) {
    setEditLevel(l);
    setFormError(null);
    setModalOpen(true);
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const name = (fd.get('name') as string || '').trim();
    const category = (fd.get('category') as string || '').trim() || null;
    const description = (fd.get('description') as string || '').trim() || null;
    const sort_order = Number.parseInt(fd.get('sort_order') as string || '0', 10) || 0;
    const active = fd.get('active') === 'on';

    if (!name) {
      setFormError('Lütfen seviye adı girin.');
      return;
    }

    setFormError(null);
    setSaving(true);
    try {
      const payload = {
        name,
        category,
        description,
        sort_order,
        active,
        updated_at: new Date().toISOString(),
      };

      if (editLevel) {
        const { error: updateError } = await supabase
          .from('zehrahoca_levels')
          .update(payload)
          .eq('id', editLevel.id);
        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('zehrahoca_levels')
          .insert({ ...payload, created_at: new Date().toISOString() });
        if (insertError) throw insertError;
      }

      form.reset();
      setModalOpen(false);
      fetchLevels();
    } catch (err) {
      setFormError((err as Error).message || 'Seviye kaydedilirken bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(l: Level) {
    try {
      const { error: updateError } = await supabase
        .from('zehrahoca_levels')
        .update({ active: !l.active, updated_at: new Date().toISOString() })
        .eq('id', l.id);
      if (updateError) throw updateError;
      fetchLevels();
    } catch (err) {
      setError((err as Error).message || 'Durum güncellenirken bir hata oluştu.');
    }
  }

  async function doDelete() {
    if (!confirmDelete) return;
    try {
      const { error: delError } = await supabase
        .from('zehrahoca_levels')
        .delete()
        .eq('id', confirmDelete.id);
      if (delError) throw delError;
      setConfirmDelete(null);
      fetchLevels();
    } catch (err) {
      setError((err as Error).message || 'Seviye silinirken bir hata oluştu.');
      setConfirmDelete(null);
    }
  }

  const activeCount = levels.filter((l) => l.active).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-heading text-base font-bold text-foreground-950">Seviye Ayarları</h2>
          <p className="text-xs text-foreground-500">
            {levels.length} seviye · {activeCount} aktif · Grup, öğrenci ve ders seçimlerinde bu liste kullanılır.
          </p>
        </div>
        <button
          type="button"
          onClick={handleNew}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-600 cursor-pointer"
        >
          <i className="ri-add-line text-base" /> Yeni Seviye Ekle
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-500" />
        </div>
      ) : error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
          <p className="text-sm font-medium text-red-700">Seviyeler yüklenirken bir hata oluştu.</p>
          <p className="mt-1 text-xs text-red-500">{error}</p>
          <button
            type="button"
            onClick={fetchLevels}
            className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-refresh-line" /> Yeniden Dene
          </button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-background-200 bg-white">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-background-200 bg-background-100/60 text-xs text-foreground-500">
                <th className="px-4 py-3 font-semibold">Seviye</th>
                <th className="px-4 py-3 font-semibold">Kategori</th>
                <th className="px-4 py-3 font-semibold">Sıra</th>
                <th className="px-4 py-3 font-semibold">Durum</th>
                <th className="px-4 py-3 text-right font-semibold">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {levels.map((l) => (
                <tr key={l.id} className="border-b border-background-100 last:border-0 hover:bg-background-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-foreground-900">{l.name}</p>
                    {l.description && <p className="mt-0.5 text-xs text-foreground-500">{l.description}</p>}
                  </td>
                  <td className="px-4 py-3 text-foreground-700">{l.category ?? '—'}</td>
                  <td className="px-4 py-3 text-foreground-600">{l.sort_order}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                        l.active ? 'bg-emerald-50 text-emerald-600' : 'bg-background-100 text-foreground-500'
                      }`}
                    >
                      <i className={l.active ? 'ri-checkbox-circle-fill' : 'ri-pause-circle-line'} />
                      {l.active ? 'Aktif' : 'Pasif'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        type="button"
                        onClick={() => toggleActive(l)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-background-100 text-foreground-600 transition hover:bg-background-200 cursor-pointer"
                        title={l.active ? 'Pasife al' : 'Aktife al'}
                      >
                        <i className={l.active ? 'ri-pause-circle-line text-base' : 'ri-play-circle-line text-base'} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleEdit(l)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-background-100 text-foreground-600 transition hover:bg-background-200 cursor-pointer"
                        title="Düzenle"
                      >
                        <i className="ri-edit-line text-base" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(l)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 text-red-500 transition hover:bg-red-100 cursor-pointer"
                        title="Sil"
                      >
                        <i className="ri-delete-bin-line text-base" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editLevel ? 'Seviye Düzenle' : 'Yeni Seviye Ekle'}
        subtitle={editLevel ? 'Seviye bilgilerini güncelleyin.' : 'Panele yeni bir seviye tanımlayın.'}
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Seviye Adı</label>
              <input
                type="text"
                name="name"
                defaultValue={editLevel?.name ?? ''}
                required
                placeholder="Örn: AGS"
                className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Kategori</label>
              <select
                name="category"
                defaultValue={editLevel?.category ?? ''}
                className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
              >
                <option value="">Seçin</option>
                {LEVEL_CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Sıra Numarası</label>
              <input
                type="number"
                name="sort_order"
                defaultValue={editLevel?.sort_order ?? 0}
                className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Açıklama</label>
              <textarea
                name="description"
                defaultValue={editLevel?.description ?? ''}
                rows={2}
                maxLength={200}
                placeholder="Seviye hakkında kısa açıklama..."
                className="w-full resize-none rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>
          </div>

          <label className="flex cursor-pointer items-center gap-2 rounded-lg bg-background-50 p-4 text-sm text-foreground-800">
            <input
              type="checkbox"
              name="active"
              defaultChecked={editLevel?.active ?? true}
              className="h-4 w-4 rounded border-background-300 text-primary-500 focus:ring-primary-400"
            />
            Seviye aktif (listelerde görünür)
          </label>

          {formError && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3.5 py-3 text-sm text-red-700">
              <i className="ri-error-warning-line mt-0.5 text-base" />
              <span>{formError}</span>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 border-t border-background-200 pt-5">
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              className="rounded-lg px-4 py-2.5 text-sm font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer whitespace-nowrap"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-600 disabled:opacity-70 cursor-pointer whitespace-nowrap"
            >
              {saving ? 'Kaydediliyor...' : editLevel ? 'Güncelle' : 'Seviyeyi Kaydet'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title="Seviyeyi Sil"
        subtitle="Bu işlem geri alınamaz."
      >
        <div className="space-y-5">
          <p className="text-sm text-foreground-700">
            <span className="font-semibold">{confirmDelete?.name}</span> seviyesini silmek istediğinize emin misiniz?
          </p>
          <div className="flex items-center justify-end gap-3 border-t border-background-200 pt-5">
            <button
              type="button"
              onClick={() => setConfirmDelete(null)}
              className="rounded-lg px-4 py-2.5 text-sm font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer whitespace-nowrap"
            >
              Vazgeç
            </button>
            <button
              type="button"
              onClick={doDelete}
              className="rounded-lg bg-red-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-red-600 cursor-pointer whitespace-nowrap"
            >
              Sil
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}